// tslint:disable: max-line-length
import * as React from 'react'
import { Provider, useSelector } from 'react-redux'
import { Redirect, Route, Switch } from 'react-router-dom'
import { ConnectedRouter } from 'connected-react-router'

import history from './history'

import store from './store'
import Auth from './auth/Auth'
import Login from './auth/Login'
import Header from './common/header/Header'
import Accounts from './accounts/Accounts'
import InternalUsers from './users/InternalUsers'
import InternalMessages from './messages/InternalMessages'
import Species from './species/Species'
import AccountUsers from './accounts/accountUsers/AccountUsers'
import AccountSettings from './accounts/accountSettings/AccountSettings'
import AccountContext from './accounts/AccountContext'
import Meta from './auth/Meta'
import ManagerUsers from './manager/users/Users'
import ManagerGroups from './manager/managerGroups'
import ManagerInventory from './manager/inventory/Inventory'
import ManagerInboundInventories from './manager/inboundInventories'
import ManagerInboundInventory from './manager/inboundInventory'
import ManagerOrders from './manager/orders/Orders'
import ManagerOrder from './manager/orders/Order/Order'
import ShoppingCart from './manager/orders/Order/ShoppingCart'
import { getShouldRenderTruckInventoryFeature } from './common/getShouldRenderTruckInventoryFeature'
import { getShouldRenderBinInventoryFeature } from './common/getShouldRenderBinInventoryFeature'
import Locations from './accounts/locations/Locations'
import LocationForm from './accounts/locations/LocationForm'
import PowerBiEmbed from './manager/powerbi'
import Docs from './docs/Docs'
import { CycleCounts, CycleCountEdit } from './accounts/cycleCounts'
import LocationInventory from './accounts/locations/LocationInventory'
import Plan from './plan/Plan'
import Order from './orders/Order'
import Orders from './orders/Orders'
import { managerBranchSelector } from './manager/managerSelectors'
import { IUser } from './types/user'
import WebChat from './components/WebChat'

const ManagerRoutes: React.FC<{ user: IUser }> = ({ user }) => {
  const managerBranch = useSelector(managerBranchSelector)
  const shouldRenderTruckInventoryFeature =
    getShouldRenderTruckInventoryFeature(
      user?.account?.truckInventoryFeature,
      managerBranch?.truckInventoryFeature,
    )
  const shouldRenderBinInventoryFeature = getShouldRenderBinInventoryFeature(
    user?.account?.binInventoryFeature,
    managerBranch?.binInventoryFeature,
  )

  return (
    <>
      <Route path="/users" component={ManagerUsers} />
      <Route path="/groups" component={ManagerGroups} />
      <Route
        path="/inbound_inventories/:inboundInventoryId"
        component={ManagerInboundInventory}
      />
      <Route
        path="/inbound_inventories"
        component={ManagerInboundInventories}
      />
      {(shouldRenderTruckInventoryFeature ||
        shouldRenderBinInventoryFeature) && (
        <>
          <Route exact path="/locations" component={Locations} />
          <Route exact path="/locations-add" component={LocationForm} />
          <Route
            exact
            path="/locations/:locationId"
            component={LocationInventory}
          />
          <Route
            exact
            path="/locations/:locationId/edit"
            component={LocationForm}
          />
          <Route exact path="/cycle_counts" component={CycleCounts} />
          <Route
            path="/cycle_counts/:cycleCountId"
            component={CycleCountEdit}
          />
        </>
      )}
      <Route path="/place_order" component={ShoppingCart} />
      <Route exact path="/inventory" component={ManagerInventory} />
      <Route path="/plan/:productId?" component={Plan} />
      <Route path="/orders/:orderId" component={ManagerOrder} />
      <Route path="/orders" component={ManagerOrders} />
      <Route exact path="/requests" render={() => <Redirect to="/orders" />} />
      <Route
        exact
        path="/"
        render={() => (
          <Redirect to={user.generalManager ? '/orders' : '/inventory'} />
        )}
      />
      <Route path="/powerbi" component={PowerBiEmbed} />
      <Route path="/docs" component={Docs} />
    </>
  )
}

const App: React.FunctionComponent = () => {
  return (
    <Provider store={store}>
      <ConnectedRouter history={history}>
        <Meta>
          <Switch>
            <Route path="/login" component={Login} />
            <Route
              render={() => (
                <Auth>
                  {(user) => (
                    <div className="app-shell">
                      <Header />
                      <div className="app-shell_body">
                        <Switch>
                          {user.role === 'manager' ? (
                            <ManagerRoutes user={user} />
                          ) : (
                            <>
                              <Route
                                path="/clients/:accountId/"
                                strict
                                render={({ match }) => (
                                  <AccountContext.Provider
                                    value={Number(match.params.accountId)}
                                  >
                                    <Switch>
                                      <Route
                                        path="/clients/:accountId/users"
                                        component={AccountUsers}
                                      />
                                      <Route
                                        path="/clients/:accountId/settings"
                                        component={AccountSettings}
                                      />
                                      <Route
                                        path="/clients/:accountId/branches/:branchId/users"
                                        component={ManagerUsers}
                                      />
                                      <Route
                                        path="/clients/:accountId/branches/:branchId/groups"
                                        component={ManagerGroups}
                                      />
                                      <Route
                                        path="/clients/:accountId/branches/:branchId/inventory"
                                        component={ManagerInventory}
                                      />
                                      <Route
                                        path="/clients/:accountId/branches/:branchId/inbound_inventories/:inboundInventoryId"
                                        component={ManagerInboundInventory}
                                      />
                                      <Route
                                        path="/clients/:accountId/branches/:branchId/inbound_inventories"
                                        component={ManagerInboundInventories}
                                      />
                                      <Route
                                        path="/clients/:accountId/branches/:branchId/plan/:productId?"
                                        component={Plan}
                                      />
                                      <Route
                                        exact
                                        path="/clients/:accountId/branches/:branchId/locations"
                                        component={Locations}
                                      />
                                      <Route
                                        exact
                                        path="/clients/:accountId/branches/:branchId/locations-add"
                                        component={LocationForm}
                                      />
                                      <Route
                                        exact
                                        path="/clients/:accountId/branches/:branchId/locations/:locationId"
                                        component={LocationInventory}
                                      />
                                      <Route
                                        exact
                                        path="/clients/:accountId/branches/:branchId/locations/:locationId/edit"
                                        component={LocationForm}
                                      />
                                      <Route
                                        exact
                                        path="/clients/:accountId/branches/:branchId/cycle_counts"
                                        component={CycleCounts}
                                      />
                                      <Route
                                        exact
                                        path="/clients/:accountId/branches/:branchId/cycle_counts/:cycleCountId"
                                        component={CycleCountEdit}
                                      />
                                      <Route
                                        path="/clients/:accountId/branches/:branchId/orders/:orderId"
                                        component={ManagerOrder}
                                      />
                                      <Route
                                        path="/clients/:accountId/branches/:branchId/orders"
                                        component={ManagerOrders}
                                      />
                                      <Route
                                        exact
                                        path="/clients/:accountId/branches/:branchId/requests"
                                        render={() => (
                                          <Redirect to="/clients/:accountId/branches/:branchId/orders" />
                                        )}
                                      />
                                      <Route
                                        exact
                                        path="/clients/:accountId/branches/:branchId"
                                        render={({ match }) => (
                                          <Redirect
                                            to={`/clients/${match.params.accountId}/branches/${match.params.branchId}/plan`}
                                          />
                                        )}
                                      />
                                    </Switch>
                                  </AccountContext.Provider>
                                )}
                              ></Route>
                              <Route path="/clients" component={Accounts} />
                              {user.role === 'internal' && (
                                <>
                                  <Route
                                    path="/orders"
                                    exact
                                    component={Orders}
                                  />
                                  <Route
                                    path="/orders/:id"
                                    exact
                                    component={Order}
                                  />
                                </>
                              )}
                              <Route path="/users" component={InternalUsers} />
                              <Route
                                path="/messages"
                                component={InternalMessages}
                              />
                              <Route
                                path="/product-attributes"
                                component={Species}
                              />
                              <Route path="/docs" component={Docs} />
                              <Route
                                exact
                                path="/"
                                render={() => <Redirect to="/clients" />}
                              />
                            </>
                          )}
                        </Switch>
                      </div>
                      <WebChat />
                    </div>
                  )}
                </Auth>
              )}
            />
          </Switch>
        </Meta>
      </ConnectedRouter>
    </Provider>
  )
}

export default App
// tslint:enable: max-line-length
